import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

import { useEffect, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import API from "../api";

ChartJS.register(LineElement, BarElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

const generarColor = (indice) => {
  const colores = [
    "rgba(255, 99, 132, 1)",
    "rgba(54, 162, 235, 1)",
    "rgba(255, 206, 86, 1)",
    "rgba(75, 192, 192, 1)",
    "rgba(153, 102, 255, 1)",
    "rgba(255, 159, 64, 1)",
    "rgba(100, 255, 100, 1)",
    "rgba(200, 100, 255, 1)"
  ];
  return colores[indice % colores.length];
};

const GraficoVendedores = ({ responsable, anios = [], meses = [] }) => {
  const [datos, setDatos] = useState([]);

  const limpiarMonto = (valor) => {
    if (!valor) return 0;
    if (typeof valor !== "string") valor = String(valor);
    const limpio = valor.replace(/[$.\s]/g, "");
    const numero = parseFloat(limpio);
    return isNaN(numero) ? 0 : numero;
  };

  useEffect(() => {
    const url = `${API}/CotizacionesOficial`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        const transformado = data
          .filter(d => d["Fecha Solicitud"]?.$date && d.Total)
          .map((d) => {
            const rawFecha = d["Fecha Solicitud"]?.$date;
            const fecha = rawFecha ? new Date(rawFecha) : null;
            const anio = fecha ? fecha.getFullYear() : null;
            const mes = fecha ? fecha.getMonth() + 1 : null;
            const total = limpiarMonto(d.Total);

            return {
              anio,
              mes,
              total,
              responsable: d.Responsable || ""
            };
          });

        setDatos(transformado);
      });
  }, []);

  const datosFiltrados = datos.filter((item) => {
    const coincideAnio = anios.length === 0 || anios.includes(item.anio);
    const coincideMes = meses.length === 0 || meses.includes(item.mes);
    const coincideResponsable = !responsable || item.responsable === responsable;
    return coincideAnio && coincideMes && coincideResponsable;
  });

  const datosPorAño = {};
  datosFiltrados.forEach(({ anio, mes, total }) => {
    if (!anio || !mes) return;
    if (!datosPorAño[anio]) datosPorAño[anio] = Array(12).fill(0);
    datosPorAño[anio][mes - 1] += total;
  });

  const datasets = Object.entries(datosPorAño).map(([anio, valores], index) => ({
    label: anio,
    data: valores,
    borderColor: generarColor(index),
    backgroundColor: "transparent",
    tension: 0.3,
  }));

  const chartData = {
    labels: [
      "Ene", "Feb", "Mar", "Abr", "May", "Jun",
      "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"
    ],
    datasets,
  };

  // Cotizaciones por mes (cantidad)
  const cantidadPorMes = {};
  datosFiltrados.forEach(({ anio, mes }) => {
    if (!anio || !mes) return;
    if (!cantidadPorMes[anio]) cantidadPorMes[anio] = Array(12).fill(0);
    cantidadPorMes[anio][mes - 1] += 1;
  });

  const barDatasets = Object.entries(cantidadPorMes).map(([anio, valores], index) => ({
    label: anio,
    data: valores,
    backgroundColor: generarColor(index),
  }));

  const barData = {
    labels: chartData.labels,
    datasets: barDatasets,
  };

  // Totales para tarjetas
  const totalGeneral = datos.reduce((sum, d) => sum + d.total, 0);
  const cantidadGeneral = datos.length;

  const totalFiltrado = datosFiltrados.reduce((sum, d) => sum + d.total, 0);
  const cantidadFiltrada = datosFiltrados.length;

  const porcentajeMonto = totalGeneral > 0 ? (totalFiltrado / totalGeneral) * 100 : 0;
  const porcentajeCantidad = cantidadGeneral > 0 ? (cantidadFiltrada / cantidadGeneral) * 100 : 0;

  return (
    <div className="bg-white p-4 rounded shadow mb-4 max-w-full">
      <Row className="mb-4">
        <Col md={3}><Card className="text-center shadow-sm"><Card.Body><h6 className="fw-bold">Cantidad total Cotizaciones</h6><p className="fs-5">{cantidadFiltrada}</p></Card.Body></Card></Col>
        <Col md={3}><Card className="text-center shadow-sm"><Card.Body><h6 className="fw-bold">Monto total Cotizaciones</h6><p className="fs-5">${totalFiltrado.toLocaleString("es-CL")}</p></Card.Body></Card></Col>
        <Col md={3}><Card className="text-center shadow-sm"><Card.Body><h6 className="fw-bold">% Cantidad</h6><p className="fs-5 text-primary">{porcentajeCantidad.toFixed(1)}%</p></Card.Body></Card></Col>
        <Col md={3}><Card className="text-center shadow-sm"><Card.Body><h6 className="fw-bold">% Monto</h6><p className="fs-5 text-primary">{porcentajeMonto.toFixed(1)}%</p></Card.Body></Card></Col>
      </Row>

      <h3 className="text-xl font-semibold mb-4 text-center">Cotizaciones totales por mes y año ($)</h3>
      <div className="w-full max-w-[1200px] h-[300px] mx-auto">
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "top" },
              title: {
                display: false,
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function (value) {
                    return "$" + value.toLocaleString("es-CL");
                  },
                },
              },
            },
          }}
        />
      </div>

      <div className="w-full max-w-[1200px] h-[300px] mx-auto mt-5">
        <h5 className="text-center mb-3">Cantidad de Cotizaciones por mes y año</h5>
        <Bar
          data={barData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "top" },
            },
            scales: {
              y: {
                beginAtZero: true,
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default GraficoVendedores;
